import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Select, MenuItem,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, FormControl, InputLabel, DialogContentText, CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Edit, Cancel, Payment as PaymentIcon, Delete, Download, Visibility, Mail, Block, Lock, PlayCircleOutline, Gavel } from '@mui/icons-material';

const PatientDetails = () => {
  const { id } = useParams();
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [viewFile, setViewFile] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [refundOpen, setRefundOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [banOpen, setBanOpen] = useState(false);
  const [unbanOpen, setUnbanOpen] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [activationReason, setActivationReason] = useState('');
  const [banReason, setBanReason] = useState('');
  const [unbanReason, setUnbanReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [newDateTime, setNewDateTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!adminUser) {
        navigate('/admin');
        return;
      }
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      setLoading(true);
      try {
        const [patientRes, appointmentsRes, feedbackRes, historyRes] = await Promise.all([
          api.get(`/admin/users/${id}`, config),
          api.get(`/admin/appointments/patient/${id}`, config),
          api.get(`/admin/feedback/patient/${id}`, config),
          api.get(`/admin/appointments/patient/${id}/history`, config)
        ]);
        setPatient(patientRes.data);
        setAppointments(appointmentsRes.data || []);
        setFeedbacks(feedbackRes.data || []);
        setPrescriptions(historyRes.data?.prescriptions || []);
        setEditForm(patientRes.data);
      } catch (err) {
        console.error('Patient details fetch error:', {
          status: err.response?.status,
          data: err.response?.data,
          message: err.message,
        });
        let errorMessage = 'Failed to fetch patient data';
        if (err.response) {
          const { status, data } = err.response;
          errorMessage = `${status}: ${data.error || 'Unknown error'}${data.details ? ` - ${data.details}` : ''}`;
          if (status === 401 || status === 403) {
            navigate('/admin');
            return;
          }
        } else if (err.request) {
          errorMessage = 'Network error: No response from server';
        } else {
          errorMessage = `Error: ${err.message}`;
        }
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, adminUser, navigate]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleCancel = (appointmentId) => {
    setSelectedAppointment(appointments.find(app => app._id === appointmentId));
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!selectedAppointment || !cancelReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/cancel`, { reason: cancelReason }, config);
      console.log('Cancel successful:', response.data);
      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment canceled successfully', severity: 'success' });
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Cancel error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to cancel appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReschedule = (appointmentId) => {
    setSelectedAppointment(appointments.find(app => app._id === appointmentId));
    setRescheduleDialogOpen(true);
  };

  const confirmReschedule = async () => {
    if (!selectedAppointment || !newDateTime || !rescheduleReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/reschedule`, 
        { newDate: newDateTime, reason: rescheduleReason }, 
        config
      );
      console.log('Reschedule successful:', response.data);
      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment rescheduled successfully', severity: 'success' });
      setRescheduleDialogOpen(false);
      setNewDateTime('');
      setRescheduleReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Reschedule error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to reschedule appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditSave = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const updatedPatient = await api.put(`/admin/users/${id}`, editForm, config);
      setPatient(updatedPatient.data);
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Patient profile updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Edit error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to update patient profile', severity: 'error' });
    }
  };

  const handleRefund = async (appointmentId) => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.post(`/admin/payments/refund/${appointmentId}`, {}, config);
      setAppointments(appointments.map(app => 
        app._id === appointmentId ? { 
          ...app, 
          paymentDetails: { ...app.paymentDetails, status: 'refunded' }, 
          refundStatus: 'Processed', 
          refundedAt: new Date(),
          refundDetails: { ...app.refundDetails, status: 'Processed', createdAt: new Date() }
        } : app
      ));
      setRefundOpen(false);
      setSnackbar({ open: true, message: 'Refund processed successfully', severity: 'success' });
    } catch (err) {
      console.error('Refund error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to process refund', severity: 'error' });
    }
  };

  const handleSendNotification = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.post(`/admin/notifications/${id}`, { message: notificationMessage }, config);
      setNotificationOpen(false);
      setNotificationMessage('');
      setSnackbar({ open: true, message: 'Notification sent successfully', severity: 'success' });
    } catch (err) {
      console.error('Notification error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to send notification', severity: 'error' });
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/patients/deactivate/${id}`, { reason: deactivationReason }, config);
      setPatient({ ...patient, accountStatus: 'deactivated', deactivationReason });
      setDeactivateOpen(false);
      setDeactivationReason('');
      setSnackbar({ open: true, message: response.data.message || 'Patient account deactivated', severity: 'success' });
    } catch (err) {
      console.error('Deactivate error:', err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to deactivate patient', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/patients/activate/${id}`, { reason: activationReason }, config);
      setPatient({ ...patient, accountStatus: 'active', deactivationReason: '' });
      setActivateOpen(false);
      setActivationReason('');
      setSnackbar({ open: true, message: response.data.message || 'Patient account activated', severity: 'success' });
    } catch (err) {
      console.error('Activate error:', err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to activate patient', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/patients/ban/${id}`, { reason: banReason }, config);
      setPatient({ ...patient, accountStatus: 'banned', deactivationReason: banReason });
      setBanOpen(false);
      setBanReason('');
      setSnackbar({ open: true, message: response.data.message || 'Patient account banned', severity: 'success' });
    } catch (err) {
      console.error('Ban error:', err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to ban patient', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUnban = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/patients/unban/${id}`, { reason: unbanReason }, config);
      setPatient({ ...patient, accountStatus: 'active', deactivationReason: '' });
      setUnbanOpen(false);
      setUnbanReason('');
      setSnackbar({ open: true, message: response.data.message || 'Patient account unbanned', severity: 'success' });
    } catch (err) {
      console.error('Unban error:', err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to unban patient', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.post(`/admin/users/${id}/reset-password`, {}, config);
      setSnackbar({ open: true, message: 'Password reset link sent to patient email', severity: 'success' });
    } catch (err) {
      console.error('Reset password error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to send password reset link', severity: 'error' });
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.delete(`/admin/feedback/${feedbackId}`, config);
      setFeedbacks(feedbacks.filter(fb => fb._id !== feedbackId));
      setSnackbar({ open: true, message: 'Feedback deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Delete feedback error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to delete feedback', severity: 'error' });
    }
  };

  // Updated handleDownload to fetch Backblaze B2 URL for prescriptions
  const handleDownload = async (prescriptionId) => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      // Fetch the prescription to get the pdfPath
      const prescription = prescriptions.find(pres => pres._id === prescriptionId);
      if (!prescription || !prescription.pdfPath) {
        setSnackbar({ open: true, message: 'No PDF available for this prescription', severity: 'warning' });
        return;
      }

      // Assume we have an endpoint to get the Backblaze URL for prescriptions
      // This endpoint needs to be added in the backend if not already present
      const response = await api.get(`/admin/prescriptions/download/${prescriptionId}`, config);
      const { url } = response.data;

      if (url) {
        window.open(url, '_blank'); // Open the Backblaze B2 URL in a new tab
      } else {
        throw new Error('No download URL received');
      }
    } catch (err) {
      console.error('Download error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: 'Failed to download prescription', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  if (!patient && !loading) return <Typography color="error">Failed to load patient data.</Typography>;

  const upcomingAppointments = appointments.filter(app => new Date(app.date) > new Date());
  const pastAppointments = appointments.filter(app => new Date(app.date) <= new Date());
  const paymentAppointments = appointments.filter(app => 
    app.paymentDetails && ['pending', 'paid', 'refunded'].includes(app.paymentDetails.status)
  );

  const totalAmountPaid = paymentAppointments
    .filter(app => app.paymentDetails.status === 'paid')
    .reduce((sum, app) => sum + (app.paymentDetails?.amountPaid || 0), 0)
    .toFixed(2);

  const getStatusChip = (status) => {
    const statusStyles = {
      pending: { color: 'warning', label: 'Pending' },
      completed: { color: 'success', label: 'Completed' },
      rescheduled: { color: 'info', label: 'Rescheduled' },
      canceled: { color: 'error', label: 'Canceled' },
      rejected: { color: 'default', label: 'Rejected' },
      accepted: { color: 'success', label: 'Accepted' },
    };
    const style = statusStyles[status] || { color: 'default', label: status };
    return (
      <Chip
        label={style.label}
        color={style.color}
        size="small"
        sx={{ fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', minWidth: '90px' }}
      />
    );
  };

  const getPaymentStatusChip = (status) => {
    const paymentStyles = {
      paid: { color: 'success', label: 'Paid' },
      pending: { color: 'warning', label: 'Pending' },
      refunded: { color: 'error', label: 'Refunded' },
    };
    const style = paymentStyles[status] || { color: 'default', label: status || 'N/A' };
    return (
      <Chip
        label={style.label}
        color={style.color}
        size="small"
        sx={{ fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', minWidth: '90px' }}
      />
    );
  };

  const isActionEnabled = (status) => ['rejected', 'pending', 'rescheduled', 'accepted'].includes(status);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Patient Details: {patient?.name || 'Loading...'}</Typography>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{ mb: 3, minWidth: { xs: 700, md: 'auto' } }}
            >
              <Tab label="Profile" />
              <Tab label="Appointments" />
              <Tab label="Payments" />
              <Tab label="Medical Records" />
              <Tab label="Notifications" />
              <Tab label="Feedback" />
              <Tab label="Actions" />
            </Tabs>
          </Box>

          {tabValue === 0 && patient && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Name:</strong> {patient.name}</Typography>
                <Typography><strong>Email:</strong> {patient.email}</Typography>
                <Typography><strong>Mobile Number:</strong> {patient.phone}</Typography>
                <Typography><strong>Address:</strong> {patient.address}, {patient.city}, {patient.state}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Age:</strong> {patient.age}</Typography>
                <Typography><strong>Gender:</strong> {patient.gender}</Typography>
                <Typography><strong>Status:</strong> <Chip label={patient.accountStatus || 'active'} color={patient.accountStatus === 'banned' ? 'error' : patient.accountStatus === 'deactivated' ? 'warning' : 'success'} /></Typography>
                {(patient.accountStatus === 'deactivated' || patient.accountStatus === 'banned') && (
                  <Typography><strong>{patient.accountStatus === 'banned' ? 'Ban' : 'Deactivation'} Reason:</strong> {patient.deactivationReason || 'Not specified'}</Typography>
                )}
                <Button startIcon={<Edit />} onClick={() => setEditOpen(true)} sx={{ mt: 2 }}>Edit Profile</Button>
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6">Appointment History</Typography>
              <Typography variant="subtitle1" mt={2}>Upcoming Appointments</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcomingAppointments.map((app) => (
                    <TableRow key={app._id}>
                      <TableCell>{new Date(app.date).toLocaleString()}</TableCell>
                      <TableCell>{app.doctorId?.name || 'N/A'}</TableCell>
                      <TableCell>{getStatusChip(app.status)}</TableCell>
                      <TableCell>
                        {isActionEnabled(app.status) && (
                          <>
                            <Button onClick={() => handleCancel(app._id)} color="error" sx={{ mr: 1 }}>Cancel</Button>
                            <Button onClick={() => handleReschedule(app._id)} color="info" sx={{ mr: 1 }}>Reschedule</Button>
                          </>
                        )}
                        <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/appointments/${app._id}`)}>View Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle1" mt={2}>Past Appointments</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pastAppointments.map((app) => (
                    <TableRow key={app._id}>
                      <TableCell>{new Date(app.date).toLocaleString()}</TableCell>
                      <TableCell>{app.doctorId?.name || 'N/A'}</TableCell>
                      <TableCell>{getStatusChip(app.status)}</TableCell>
                      <TableCell>
                        {isActionEnabled(app.status) && (
                          <>
                            <Button onClick={() => handleCancel(app._id)} color="error" sx={{ mr: 1 }}>Cancel</Button>
                            <Button onClick={() => handleReschedule(app._id)} color="info" sx={{ mr: 1 }}>Reschedule</Button>
                          </>
                        )}
                        <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/appointments/${app._id}`)}>View Details</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6">Payment & Transaction History</Typography>
              <Typography><strong>Total Amount Paid:</strong> ₹{totalAmountPaid}</Typography>
              {paymentAppointments.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Appointment ID</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Payment Status</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Payment Date</TableCell>
                      <TableCell>Refund Date</TableCell>
                      <TableCell>Refund ID</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentAppointments.map((app) => (
                      <TableRow key={app._id}>
                        <TableCell>{app._id}</TableCell>
                        <TableCell>₹{app.paymentDetails?.amountPaid?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>{getPaymentStatusChip(app.paymentDetails?.status)}</TableCell>
                        <TableCell>{app.paymentDetails?.method || 'N/A'}</TableCell>
                        <TableCell>{app.paymentDetails?.paymentId || 'N/A'}</TableCell>
                        <TableCell>{app.paymentDetails?.paidAt ? new Date(app.paymentDetails.paidAt).toLocaleString() : 'N/A'}</TableCell>
                        <TableCell>{app.refundedAt ? new Date(app.refundedAt).toLocaleString() : 'N/A'}</TableCell>
                        <TableCell>{app.refundDetails?.refundId || 'N/A'}</TableCell>
                        <TableCell>
                          {app.paymentDetails?.status === 'paid' && app.status === 'canceled' && app.refundStatus !== 'Processed' && (
                            <Button startIcon={<PaymentIcon />} onClick={() => setRefundOpen(app._id)}>Refund</Button>
                          )}
                          {app.paymentDetails?.invoicePath && (
                            <Button startIcon={<Download />} onClick={() => handleDownload(app.paymentDetails.invoicePath)}>Invoice</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography>No payment records found.</Typography>
              )}
            </Box>
          )}

          {tabValue === 3 && (
            <Box>
              <Typography variant="h6">Medical Records</Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Previous Prescriptions</Typography>
                {prescriptions.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {prescriptions.map((pres, index) => (
                      <Box key={index} sx={{ p: 2, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                        <Typography><strong>Date Issued:</strong> {new Date(pres.createdAt).toLocaleString()}</Typography>
                        <Typography><strong>Issued By:</strong> {pres.doctorName || 'Unknown Doctor'}</Typography>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Medications:</Typography>
                        {pres.medications && pres.medications.length > 0 ? (
                          <ul style={{ margin: '0 0 10px 20px', padding: 0 }}>
                            {pres.medications.map((med, medIndex) => (
                              <li key={medIndex}>{med.name} - {med.dosage} ({med.frequency}, {med.duration})</li>
                            ))}
                          </ul>
                        ) : (
                          <Typography>No medications listed.</Typography>
                        )}
                        <Typography><strong>Instructions:</strong> {pres.instructions || 'N/A'}</Typography>
                        {pres.pdfPath && (
                          <Button 
                            startIcon={<Download />} 
                            onClick={() => handleDownload(pres._id)} 
                            sx={{ mt: 1 }}
                          >
                            Download Prescription PDF
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: '#777' }}>No prescriptions found.</Typography>
                )}
              </Box>
            </Box>
          )}

          {tabValue === 4 && (
            <Box>
              <Typography variant="h6">Notifications</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(patient.notificationHistory || []).map((notif, index) => (
                    <TableRow key={index}>
                      <TableCell>{new Date(notif.date).toLocaleString()}</TableCell>
                      <TableCell>{notif.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button startIcon={<Mail />} onClick={() => setNotificationOpen(true)} sx={{ mt: 2 }}>Send Custom Notification</Button>
            </Box>
          )}

          {tabValue === 5 && (
            <Box>
              <Typography variant="h6">Ratings & Feedback</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Doctor</TableCell>
                    <TableCell>Rating</TableCell>
                    <TableCell>Comment</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {feedbacks.length > 0 ? (
                    feedbacks.map((fb) => (
                      <TableRow key={fb._id}>
                        <TableCell>{fb.doctorName || fb.doctorId}</TableCell>
                        <TableCell>{fb.rating}</TableCell>
                        <TableCell>{fb.feedback}</TableCell>
                        <TableCell>{new Date(fb.createdAt).toLocaleString()}</TableCell>
                        <TableCell>
                          <Button startIcon={<Delete />} onClick={() => handleDeleteFeedback(fb._id)} color="error">Delete</Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>No feedback available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {tabValue === 6 && (
            <Box>
              <Typography variant="h6">Account Actions</Typography>
              {patient.accountStatus === 'active' && (
                <Button startIcon={<Block />} onClick={() => setDeactivateOpen(true)} color="warning" sx={{ mr: 2 }}>Deactivate</Button>
              )}
              {patient.accountStatus === 'deactivated' && (
                <Button startIcon={<PlayCircleOutline />} onClick={() => setActivateOpen(true)} color="success" sx={{ mr: 2 }}>Activate</Button>
              )}
              {patient.accountStatus === 'banned' && (
                <Button startIcon={<Gavel />} onClick={() => setUnbanOpen(true)} color="success" sx={{ mr: 2 }}>Unban</Button>
              )}
              <Button startIcon={<Block />} onClick={() => setBanOpen(true)} disabled={patient.accountStatus === 'banned'} color="error" sx={{ mr: 2 }}>Ban</Button>
              <Button startIcon={<Lock />} onClick={handleResetPassword}>Reset Password</Button>
            </Box>
          )}

          <Button onClick={() => navigate('/admin/patients')} sx={{ mt: 3 }}>Back</Button>
        </Paper>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Patient Profile</DialogTitle>
        <DialogContent>
          <TextField label="Name" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Mobile Number" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Address" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="City" value={editForm.city || ''} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="State" value={editForm.state || ''} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Age" value={editForm.age || ''} onChange={(e) => setEditForm({ ...editForm, age: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Gender" value={editForm.gender || ''} onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })} fullWidth sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!refundOpen} onClose={() => setRefundOpen(false)}>
        <DialogTitle>Process Refund</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to refund this appointment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundOpen(false)}>Cancel</Button>
          <Button onClick={() => handleRefund(refundOpen)} color="success">Refund</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notificationOpen} onClose={() => setNotificationOpen(false)}>
        <DialogTitle>Send Custom Notification</DialogTitle>
        <DialogContent>
          <TextField
            label="Message"
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            fullWidth
            multiline
            rows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationOpen(false)}>Cancel</Button>
          <Button onClick={handleSendNotification} color="primary">Send</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
        <DialogTitle>Deactivate Patient</DialogTitle>
        <DialogContent>
          <TextField
            label="Deactivation Reason (sent to patient via email)"
            multiline
            rows={3}
            value={deactivationReason}
            onChange={(e) => setDeactivationReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" disabled={!deactivationReason || loading}>
            {loading ? <CircularProgress size={24} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activateOpen} onClose={() => setActivateOpen(false)}>
        <DialogTitle>Activate Patient</DialogTitle>
        <DialogContent>
          <TextField
            label="Activation Reason (optional, sent to patient via email)"
            multiline
            rows={3}
            value={activationReason}
            onChange={(e) => setActivationReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleActivate} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={banOpen} onClose={() => setBanOpen(false)}>
        <DialogTitle>Ban Patient</DialogTitle>
        <DialogContent>
          <TextField
            label="Ban Reason (sent to patient via email)"
            multiline
            rows={3}
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleBan} color="error" disabled={!banReason || loading}>
            {loading ? <CircularProgress size={24} /> : 'Ban'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unbanOpen} onClose={() => setUnbanOpen(false)}>
        <DialogTitle>Unban Patient</DialogTitle>
        <DialogContent>
          <TextField
            label="Unban Reason (optional, sent to patient via email)"
            multiline
            rows={3}
            value={unbanReason}
            onChange={(e) => setUnbanReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnbanOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleUnban} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Unban'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewFile} onClose={() => setViewFile(null)} maxWidth="md" fullWidth>
        <DialogTitle>View File</DialogTitle>
        <DialogContent>
          {viewFile && (
            <iframe
              src={viewFile} // Use the Backblaze URL directly
              style={{ width: '100%', height: '500px', border: 'none' }}
              title="File Viewer"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewFile(null)}>Close</Button>
          <Button startIcon={<Download />} onClick={() => window.open(viewFile, '_blank')}>Download</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for Cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={confirmCancel} color="error" disabled={!cancelReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)}>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Date & Time"
            type="datetime-local"
            value={newDateTime}
            onChange={(e) => setNewDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
          <TextField
            fullWidth
            label="Reason for Rescheduling"
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={confirmReschedule} color="primary" disabled={!newDateTime || !rescheduleReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PatientDetails;